import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";

import ObjectSelection from "../utils/ObjectSelection";
import { ButtonUI } from "../../../Common";
import { FormTile } from "../../../Common/Tile";

import { openInNewTab } from "../../../../util/common";
import { OBJECT_TYPE_ROUTES } from "../utils/JobData";
import { DATA_SOURCE } from "../../../../util/Data/DataModeling";
import SmallToolbar from "../../../Common/SmallToolbar";

const ObjectPanel = ({
  objectType,
  objects,
  setObjects,
  loading,
  setLoading,
  getObjects,
  infoMessage = null,
  exceptReadOnly = false,
}) => {
  const { Workspace_id } = useParams();

  const selectAll = useMemo(() => objects.every((item) => item.checked), [objects]);
  const intermediate = useMemo(() => !selectAll && objects.some((item) => item.checked), [selectAll, objects]);

  const handleSelectAllChange = useCallback(
    (event) => {
      const isChecked = event.target.checked;
      setObjects((prev) => prev.map((item) => ({ ...item, checked: isChecked })));
    },
    [setObjects]
  );

  const handleCheckboxChange = useCallback(
    (event, name) => {
      const isChecked = event.target.checked;
      setObjects((prev) => prev.map((item) => (item.name === name ? { ...item, checked: isChecked } : item)));
    },
    [setObjects]
  );

  const onRefresh = useCallback(async () => {
    setLoading(true);
    const objectList = await getObjects();

    //Setting selected refresh objects
    if (objectList.length) {
      if (selectAll === true) setObjects(objectList.map((item) => ({ ...item, checked: true })));
      else
        setObjects((prevState) => {
          const idToChecked = {};
          prevState.forEach(({ id, checked = false }) => {
            idToChecked[id] = checked;
          });
          const updatedState = objectList.map((object) => {
            return {
              ...object,
              checked: idToChecked[object?.id] || false,
            };
          });

          return updatedState;
        });
    }
    setLoading(false);
  }, [getObjects, selectAll, setObjects, setLoading]);

  return (
    <>
      <SmallToolbar
        sx={{
          display: "flex",
          alignItems: "center",
        }}
        disableGutters
      >
        <Box sx={{ flexGrow: 1 }}>{`Selected ${objectType === 1 ? DATA_SOURCE.plural : "Entities"}`}</Box>
        <ButtonUI
          endIcon={<OpenInNewIcon />}
          variant="contained"
          disabled={loading}
          color="info"
          onClick={() => openInNewTab(`/my-workspaces/${Workspace_id}/${OBJECT_TYPE_ROUTES[objectType]}/add`)}
        >
          {`Add ${objectType === 1 ? DATA_SOURCE.singular : "Entity"}`}
        </ButtonUI>
      </SmallToolbar>
      <FormTile>
        <>
          <Grid container>
            <ObjectSelection
              selectAll={selectAll}
              intermediate={intermediate}
              objectList={objects}
              objectType={objectType}
              handleSelectAllChange={handleSelectAllChange}
              handleCheckboxChange={handleCheckboxChange}
              handleRefresh={onRefresh}
              loading={loading}
              exceptReadOnly={exceptReadOnly}
            />
          </Grid>
          {infoMessage}
        </>
      </FormTile>
    </>
  );
};

ObjectPanel.propTypes = {
  objectType: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  objects: PropTypes.array.isRequired,
  setObjects: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  getObjects: PropTypes.func.isRequired,
};

export default React.memo(ObjectPanel);
